import {
  defineComponent,
  h,
  ref,
  shallowRef,
  watch,
  onMounted,
  withModifiers,
} from "vue";
import { get, isString, omit } from "lodash";
import { VSelect, VBtn } from "vuetify/components";
import type { FetchPolicy } from "@apollo/client";
import type { Model, ModelConstructor } from "@moirei/dobby";
import type { ComponentInstance, PropType } from "vue";
import { AppIcon } from "#components";

// -----------------------------
// Types
// -----------------------------
type Ctx<T> = {
  result: T[];
  updating: boolean;
  select: (item: T) => void;
  readonly fetching: boolean;
};

type Instance<T> = ComponentInstance<any> & Ctx<T>;

type BaseOptions<T> = {
  name: string;
  icon: string;
  placeholder: string;
  itemTitle?: string;
  itemValue?: string;
  action?: {
    title: string;
    handler: (this: Ctx<T>) => any;
  };
};

// -----------------------------
// Base Props for All Selects
// -----------------------------
const baseProps = <T>(o: BaseOptions<T>) => {
  const x = {
    placeholder: { type: String, default: o.placeholder },
    itemTitle: { type: String, default: o.itemTitle || "name" },
    itemValue: { type: String, default: o.itemValue || "id" },
    mandatory: { type: Boolean, default: false },
    returnObject: { type: Boolean, default: false },
    modelValue: {},
  };

  return {
    ...VSelect.props,
    ...x,
  } as typeof x;
};

// -----------------------------
// Render Helper
// -----------------------------
const renderSelect = <T>(o: {
  instance: Instance<T>;
  omitProps: string[];
  icon?: string;
  action?: BaseOptions<T>["action"];
}) => {
  return h(
    VSelect,
    {
      ...omit(o.instance.$props, o.omitProps),
      ...o.instance.$attrs,
      items: o.instance.result,
      modelValue: o.instance.modelValue,
      "onUpdate:modelValue": (v: any) =>
        o.instance.$emit("update:modelValue", v),
    },
    {
      "prepend-inner": () => {
        if (o.icon) {
          return h(AppIcon, { name: o.icon, size: 20 });
        }
      },
      "append-inner": () => {
        if (o.instance.fetching || o.instance.updating) {
          return h(VBtn, {
            loading: true,
            icon: true,
            size: 20,
            variant: "text",
          });
        } else if (o.action) {
          return h(VBtn, {
            class: "text-none",
            text: o.action.title,
            elevation: 1,
            onClick: withModifiers(o.action.handler.bind(o.instance as any), [
              "prevent",
            ]),
          });
        }
      },
    }
  );
};

// -----------------------------
// Model-Driven Select Component
// -----------------------------
export const defineModelSelectComponent = <T extends Model>(
  o: BaseOptions<T> & {
    model: ModelConstructor<T>;
  }
) => {
  return defineComponent({
    name: o.name,
    props: {
      ...baseProps(o),
      additionalSelect: {
        type: [Array, String] as PropType<string | string[]>,
      },
      channel: { type: String },
      cachePolicy: {
        type: String as PropType<FetchPolicy>,
        default: "cache-first",
      },
    },
    setup(props, ctx) {
      const updating = ref(false);

      const query = o.model.select(props.itemTitle, props.itemValue);

      if (props.additionalSelect) {
        query.add(props.additionalSelect);
      }

      if (props.channel) {
        query.where("publishedTo", props.channel);
      }

      if (props.cachePolicy) {
        query.policy(props.cachePolicy);
      }

      const { loading: fetching, items: result } = useListQuery(query);

      const select = (item: unknown) => {
        const value = props.returnObject ? item : get(item, props.itemValue);
        ctx.emit("update:modelValue", value);
      };

      const stop = watch(result, (items) => {
        if (items.length && props.mandatory && !props.modelValue) {
          select(items[0]);
          stop(); // stop the watcher
        }
      });

      return {
        updating,
        result,
        fetching,
        select,
      };
    },
    render() {
      return renderSelect({
        instance: this,
        action: o.action,
        icon: o.icon,
        omitProps: ["mandatory", "additionalSelect", "channel", "cachePolicy"],
      });
    },
  });
};

// -----------------------------
// Query-Driven Select Component
// -----------------------------
export const defineQuerySelectComponent = <T extends Model>(
  o: BaseOptions<T> & {
    fetch: () => Promise<T[]>;
  }
) => {
  return defineComponent({
    name: o.name,
    props: baseProps(o),
    setup(props, ctx) {
      const updating = ref(false);
      const fetching = ref(false);
      const result = shallowRef<T[]>([]);

      const select = (item: unknown) => {
        const value = props.returnObject ? item : get(item, props.itemValue);
        ctx.emit("update:modelValue", value);
      };

      const fetch = async () => {
        fetching.value = true;
        const items = await o.fetch();
        result.value = items;
        fetching.value = false;

        if (items.length && props.mandatory && !props.modelValue) {
          select(items[0]);
        }
      };

      onMounted(fetch);

      return {
        updating,
        result,
        fetching,
        select,
        fetch,
      };
    },
    render() {
      return renderSelect({
        instance: this,
        action: o.action,
        icon: o.icon,
        omitProps: ["mandatory", "additionalSelect"],
      });
    },
  });
};
